import React from 'react';
import { CheckCircle2, Search } from 'lucide-react';

import { CoursePicker } from 'components/admin/CoursePicker';
import { FlowPicker } from 'components/admin/FlowPicker';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { ScrollArea } from 'components/ui/scroll-area';
import { Spinner } from 'components/ui/spinner';
import { Switch } from 'components/ui/switch';
import { cn } from 'lib/utils';
import type { OrganizationAdminResponseQuestion } from 'types';

import { getQuestionContext, getQuestionTypeBadgeClassName, getTypeLabel } from './reporting-utils';

interface QuestionPickerProps {
  questions: OrganizationAdminResponseQuestion[];
  selectedQuestionIds: number[];
  search: string;
  courseFilter: string;
  moduleFilter: string;
  questionTypeFilter: string;
  courseOptions: Array<{ id: number; name: string }>;
  moduleOptions: Array<{ id: number; name: string }>;
  questionTypeOptions: Array<{ value: string; label: string }>;
  isLoading: boolean;
  showSelectedOnly: boolean;
  onSearchChange: (value: string) => void;
  onCourseFilterChange: (value: string) => void;
  onModuleFilterChange: (value: string) => void;
  onQuestionTypeFilterChange: (value: string) => void;
  onShowSelectedOnlyChange: (value: boolean) => void;
  onToggleQuestion: (questionId: number) => void;
  onSelectAllQuestions: () => void;
  onDeselectAllQuestions: () => void;
}

export const QuestionPicker: React.FC<QuestionPickerProps> = ({
  questions,
  selectedQuestionIds,
  search,
  courseFilter,
  moduleFilter,
  questionTypeFilter,
  courseOptions,
  moduleOptions,
  questionTypeOptions,
  isLoading,
  showSelectedOnly,
  onSearchChange,
  onCourseFilterChange,
  onModuleFilterChange,
  onQuestionTypeFilterChange,
  onShowSelectedOnlyChange,
  onToggleQuestion,
  onSelectAllQuestions,
  onDeselectAllQuestions,
}) => {
  const visibleQuestionIds = questions.map((question) => question.id);
  const visibleSelectedCount = visibleQuestionIds.filter((id) => selectedQuestionIds.includes(id)).length;
  const hasVisibleQuestions = visibleQuestionIds.length > 0;
  const emptyTitle = showSelectedOnly ? 'No selected questions in this view' : 'No reportable questions found';
  const emptyDescription = showSelectedOnly
    ? 'Clear the current search or filters, or return to all questions to select more.'
    : 'Try a different search or check whether users have completed supported question types.';

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Choose Questions</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Search and filter answered, reportable questions from this organization.
        </p>
      </div>

      <label className="relative block">
        <span className="sr-only">Search reportable questions</span>
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search questions, keys, modules..."
          className="pl-10"
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="response-question-course-filter">Course</Label>
          <CoursePicker
            value={courseFilter}
            onValueChange={onCourseFilterChange}
            options={courseOptions.map((course) => ({
              value: String(course.id),
              label: course.name,
            }))}
            placeholder="All courses"
            triggerId="response-question-course-filter"
            sentinelOption={{ value: 'all', label: 'All courses' }}
            emptyMessage="No courses found"
            maxVisibleOptions={50}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="response-question-module-filter">Module</Label>
          <FlowPicker
            value={moduleFilter}
            onValueChange={onModuleFilterChange}
            options={moduleOptions.map((module) => ({
              value: String(module.id),
              label: module.name,
            }))}
            placeholder="All modules"
            searchPlaceholder="Search modules..."
            triggerId="response-question-module-filter"
            sentinelOption={{ value: 'all', label: 'All modules' }}
            emptyMessage="No modules found"
            maxVisibleOptions={50}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="response-question-type-filter">Question Type</Label>
          <CoursePicker
            value={questionTypeFilter}
            onValueChange={onQuestionTypeFilterChange}
            options={questionTypeOptions}
            placeholder="All question types"
            searchPlaceholder="Search question types..."
            triggerId="response-question-type-filter"
            sentinelOption={{ value: 'all', label: 'All question types' }}
            emptyMessage="No question types found"
            maxVisibleOptions={50}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {visibleSelectedCount} of {visibleQuestionIds.length} visible questions selected
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="response-question-show-selected"
              checked={showSelectedOnly}
              onCheckedChange={onShowSelectedOnlyChange}
            />
            <Label
              htmlFor="response-question-show-selected"
              className="cursor-pointer text-xs text-muted-foreground"
            >
              Show selected questions
            </Label>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="auto"
            className="px-4 py-2 text-xs"
            onClick={onSelectAllQuestions}
            disabled={!hasVisibleQuestions || visibleSelectedCount === visibleQuestionIds.length}
          >
            Select all
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="auto"
            className="px-4 py-2 text-xs"
            onClick={onDeselectAllQuestions}
            disabled={!hasVisibleQuestions || visibleSelectedCount === 0}
          >
            Deselect all
          </Button>
        </div>
      </div>

      <ScrollArea className="h-[26rem] pr-3">
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Spinner size="sm" />
            </div>
          ) : null}

          {!isLoading && questions.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-muted/20 px-5 py-8 text-center">
              <p className="text-sm font-medium text-foreground">{emptyTitle}</p>
              <p className="mt-1 text-xs text-muted-foreground">{emptyDescription}</p>
            </div>
          ) : null}

          {!isLoading && questions.map((question) => {
            const isSelected = selectedQuestionIds.includes(question.id);
            return (
              <button
                key={question.id}
                type="button"
                aria-label={`${isSelected ? 'Remove' : 'Select'} ${question.text}`}
                onClick={() => onToggleQuestion(question.id)}
                className={cn(
                  'w-full min-w-0 overflow-hidden rounded-3xl border px-4 py-4 text-left shadow-sm transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isSelected
                    ? 'border-foundation-orange-500 bg-foundation-orange-50'
                    : 'border-border bg-white hover:-translate-y-0.5 hover:border-foundation-orange-300 hover:shadow-elevation-sm'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className={getQuestionTypeBadgeClassName(question.normalized_type)}
                        data-testid={`response-question-type-badge-${question.id}`}
                      >
                        {getTypeLabel(question.normalized_type)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {question.response_count} responses
                      </span>
                    </div>
                    <p className="whitespace-normal break-words text-sm font-semibold leading-5 text-foreground [overflow-wrap:anywhere]">
                      {question.text}
                    </p>
                    <p className="whitespace-normal break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
                      {getQuestionContext(question)}
                    </p>
                  </div>
                  <CheckCircle2
                    className={cn(
                      'mt-1 size-5 shrink-0 transition-colors',
                      isSelected ? 'text-foundation-orange-500' : 'text-muted-foreground/30'
                    )}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
